"use client";

import React from "react";
import { AvatarGroupProps } from "./types";
import { defaultAvatarGroupTheme } from "./themes";
import { mergeTheme } from "../utils/theme";

interface AvatarProps {
  /** Avatar value - can be a URL (including data URI), or a name for initials */
  avatar: string;
  type: "first" | "second";
  className: string;
}

const isUrl = (str: string): boolean => {
  return (
    str.startsWith("http://") ||
    str.startsWith("https://") ||
    str.startsWith("data:")
  );
};

const Avatar: React.FC<AvatarProps> = ({ avatar, type, className }) => {
  // Show image if avatar is a URL
  if (isUrl(avatar)) {
    // Check if it's a data URI (icon) vs regular image URL
    const isDataUri = avatar.startsWith("data:");

    return (
      <div
        className={`${className} ${isDataUri ? "flex items-center justify-center" : ""}`}
      >
        <img
          src={avatar}
          alt={`${type} avatar`}
          className={
            isDataUri ? "object-contain" : "w-full h-full object-cover"
          }
        />
      </div>
    );
  }

  // Show initials as fallback for non-URL strings
  return (
    <div className={className}>
      <span className="text-sm">{avatar.charAt(0).toUpperCase()}</span>
    </div>
  );
};

export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  firstAvatar,
  secondAvatar,
  layout,
  size = "medium",
  theme,
  className = "",
}) => {
  const mergedTheme = mergeTheme(defaultAvatarGroupTheme, theme);
  const sizeStyles = mergedTheme.sizes?.[size] || mergedTheme.sizes?.medium;

  const firstAvatarClasses = [
    mergedTheme.avatar?.base,
    mergedTheme.avatar?.first,
    mergedTheme.avatar?.placeholder,
    sizeStyles,
  ]
    .filter(Boolean)
    .join(" ");

  // Second avatar positioning based on layout
  const getSecondAvatarClasses = () => {
    const baseClasses = [
      mergedTheme.avatar?.base,
      mergedTheme.avatar?.second,
      mergedTheme.avatar?.placeholder,
      sizeStyles,
    ];

    if (layout === "horizontal") {
      // Overlapping horizontally
      baseClasses.push("-ml-4 z-[2] relative");
    } else if (layout === "diagonal") {
      // Overlapping diagonally (down and to the right)
      baseClasses.push("-ml-6 mt-6 z-[2] relative");
    }
    // horizontal-line doesn't add overlap classes

    return baseClasses.filter(Boolean).join(" ");
  };

  const secondAvatarClasses = getSecondAvatarClasses();

  // Determine if we're showing a second avatar
  // undefined = don't show, string = show
  const hasSecondAvatar = secondAvatar !== undefined;

  return (
    <>
      <style>{`
        @keyframes bounceLeftRight {
          0% {
            transform: translate(-50%, -50%) translateX(-4px) scaleX(0.5);
          }
          50% {
            transform: translate(-50%, -50%) translateX(0) scaleX(1);
          }
          100% {
            transform: translate(-50%, -50%) translateX(4px) scaleX(0.5);
          }
        }
        .avatar-separator {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
      <div
        className={`avatar-group ${layout} ${mergedTheme.container} relative ${className}`}
      >
        {/* First avatar */}
        <div className="relative z-[1]">
          <Avatar
            avatar={firstAvatar}
            type="first"
            className={firstAvatarClasses}
          />
        </div>

        {/* Separator (only shown for horizontal-line layout) */}
        {hasSecondAvatar && layout === "horizontal-line" && (
          <div className={`avatar-separator ${mergedTheme.separator}`}>
            <div
              className="animated-line bg-purple-accent"
              style={{
                position: "absolute",
                width: "16px",
                height: "2px",
                borderRadius: "10px",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                animation:
                  "bounceLeftRight 1s linear infinite alternate-reverse",
              }}
            />
          </div>
        )}

        {/* Second avatar (optional) */}
        {hasSecondAvatar && (
          <div>
            <Avatar
              avatar={secondAvatar!}
              type="second"
              className={secondAvatarClasses}
            />
          </div>
        )}
      </div>
    </>
  );
};
